import React, { useState, useRef } from "react";
import { CCard, CCardBody, CButton } from "@coreui/react";
import BarChart from "./BarChart";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";

const RegisteredUsersCard = ({
  customChartData,
  dateRange,
  setDateRange,
  customLoggedInChartData,
  loggedInDateRange,
  setLoggedInDateRange,
  totalRegistered,
  registerStatus,
  registerPercentageChange,
  totalLoggedIn,
  loggedInStatus,
  loggedInPercentageChange,
}) => {
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [tempLoggedInDateRange, setTempLoggedInDateRange] =
    useState(loggedInDateRange);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [activeButton, setActiveButton] = useState(null);
  const [activeTab, setActiveTab] = useState("registeredUsers");
  const dropdownRef = useRef(null);

  const handleDateChange = (selectedDates) => {
    if (selectedDates.length === 2) {
      setTempDateRange(selectedDates);
      setActiveButton(null);
    }
    setDropdownVisible(true);
  };

  const handleLoggedInDateChange = (selectedDates) => {
    if (selectedDates.length === 2) {
      setTempLoggedInDateRange(selectedDates);
      setActiveButton(null);
    }
    setDropdownVisible(true);
  };

  const applyDateRange = () => {
    setDateRange(tempDateRange);
    setDropdownVisible(false);
  };

  const applyLoggedInDateRange = () => {
    setLoggedInDateRange(tempLoggedInDateRange);
    setDropdownVisible(false);
  };

  const setDateRangeToToday = () => {
    const today = new Date();
    setTempDateRange([today, today]);
  };

  const setLoggedInDateRangeToToday = () => {
    const today = new Date();
    setTempLoggedInDateRange([today, today]);
  };

  const setDateRangeToThisWeek = () => {
    const today = new Date();
    const firstDayOfWeek = new Date(
      today.setDate(today.getDate() - today.getDay())
    );
    const lastDayOfWeek = new Date(
      today.setDate(today.getDate() - today.getDay() + 6)
    );
    setTempDateRange([firstDayOfWeek, lastDayOfWeek]);
  };

  const setLoggedInDateRangeToThisWeek = () => {
    const today = new Date();
    const firstDayOfWeek = new Date(
      today.setDate(today.getDate() - today.getDay())
    );
    const lastDayOfWeek = new Date(
      today.setDate(today.getDate() - today.getDay() + 6)
    );
    setTempLoggedInDateRange([firstDayOfWeek, lastDayOfWeek]);
  };

  const setDateRangeToThisMonth = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    );
    setTempDateRange([firstDayOfMonth, lastDayOfMonth]);
  };

  const setLoggedInDateRangeToThisMonth = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    );
    setTempLoggedInDateRange([firstDayOfMonth, lastDayOfMonth]);
  };

  const setDateRangeToThisYear = () => {
    const today = new Date();
    const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
    const lastDayOfYear = new Date(today.getFullYear(), 11, 31);
    setTempDateRange([firstDayOfYear, lastDayOfYear]);
  };

  const setLoggedInDateRangeToThisYear = () => {
    const today = new Date();
    const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
    const lastDayOfYear = new Date(today.getFullYear(), 11, 31);
    setTempLoggedInDateRange([firstDayOfYear, lastDayOfYear]);
  };

  const resetDateRange = () => {
    const start = new Date();
    start.setMonth(start.getMonth() - 5);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);

    const end = new Date();
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
    end.setHours(23, 59, 59, 999);

    setTempDateRange([start, end]);
  };

  const resetLoggedInDateRange = () => {
    const start = new Date();
    start.setMonth(start.getMonth() - 5);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);

    const end = new Date();
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
    end.setHours(23, 59, 59, 999);

    setTempLoggedInDateRange([start, end]);
  };

  const handleButtonClick = (rangeSetter, buttonId) => {
    rangeSetter();
    setActiveButton(buttonId);
  };

  const formatDateRange = (range) => {
    if (range.length === 2) {
      return `${range[0]
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .replace(/ /g, "-")} - ${range[1]
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .replace(/ /g, "-")}`.toUpperCase();
    }
    return "Select Date Range";
  };

  const calculateTotal = (data) => {
    if (
      !data ||
      !data.datasets ||
      !data.datasets[0] ||
      !data.datasets[0].data
    ) {
      return 0;
    }
    return data.datasets[0].data.reduce((acc, value) => acc + value, 0);
  };

  const calculatePercentageDifference = (current, previous) => {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
  };

  const formatNumber = (num) => {
    if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
    if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
    if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
    return num;
  };

  const formatPercentage = (percentage) => {
    if (
      percentage === "N/A" ||
      percentage === undefined ||
      percentage === null
    ) {
      return "N/A";
    }
    return percentage.toFixed(2);
  };

  const totalRegisteredUsers = calculateTotal(customChartData);
  const totalLoggedInUsers = calculateTotal(customLoggedInChartData);

  const registeredUsersPercentageDifference = calculatePercentageDifference(
    totalRegisteredUsers,
    totalRegistered
  );
  const loggedInUsersPercentageDifference = calculatePercentageDifference(
    totalLoggedInUsers,
    totalLoggedIn
  );

  const currentMonth = new Date().toISOString().slice(0, 7);
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  const startMonth = sixMonthsAgo.toISOString().slice(0, 7);

  return (
    <CCard className="border-light" style={{ gridRow: "span 2" }}>
      <CCardBody>
        <div style={{ overflowX: "auto" }}>
          <ul
            className="nav"
            style={{
              display: "flex",
              gap: "20px",
              listStyle: "none",
              paddingLeft: 0,
              marginBottom: "10px",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <li>
              <span
                onClick={() => setActiveTab("registeredUsers")}
                style={{
                  cursor: "pointer",
                  display: "inline-block",
                  padding: "8px 4px",
                  fontWeight: activeTab === "registeredUsers" ? 600 : 400,
                  color: activeTab === "registeredUsers" ? "#0B4FE4" : "#555E67",
                  borderBottom:
                    activeTab === "registeredUsers"
                      ? "2px solid #0B4FE4"
                      : "2px solid transparent",
                }}
              >
                Number of Registered Users
              </span>
            </li>
            <li>
              <span
                onClick={() => setActiveTab("loggedInUsers")}
                style={{
                  cursor: "pointer",
                  display: "inline-block",
                  padding: "8px 4px",
                  fontWeight: activeTab === "loggedInUsers" ? 600 : 400,
                  color: activeTab === "loggedInUsers" ? "#0B4FE4" : "#555E67",
                  borderBottom:
                    activeTab === "loggedInUsers"
                      ? "2px solid #0B4FE4"
                      : "2px solid transparent",
                }}
              >
                Number of Logged In Users
              </span>
            </li>
          </ul>
          {activeTab === "registeredUsers" && (
            <div
              style={{
                margin: "5px",
                borderRadius: "10px",
                overflowX: "auto",
              }}
            >
                <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap">
                  <div>
                    <h3>
                      <strong>{formatNumber(totalRegistered)}</strong>
                      <span
                        style={{
                          marginLeft: "10px",
                          color:
                            registerPercentageChange >= 0 ? "green" : "red",
                          fontSize: "0.6em",
                        }}
                      >
                        ({registerPercentageChange >= 0 ? "+" : ""}
                        {formatPercentage(registerPercentageChange)}%)
                      </span>
                    </h3>
                    <span className="text-muted">
                      Total Number of Registered Users
                    </span>
                  </div>
                  <div
                    ref={dropdownRef}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "6px",
                      borderRadius: "5px",
                      width: "340px",
                      margin: "10px auto",
                      marginBottom: "-20px",
                      position: "relative",
                    }}
                  >
                    <CButton
                      style={{
                        border: "0px solid lightgrey",
                        backgroundColor: "#F8F5F5",
                        borderRadius: "5px",
                        color: "#343333",
                        height: "45px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        transition: "background-color 0.3s",
                      }}
                      color="primary"
                      onClick={() => setDropdownVisible(!dropdownVisible)}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#e0e0e0";
                        const icon = e.target.querySelector("i");
                        if (icon) {
                          icon.style.backgroundColor = "#e0e0e0";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#F8F5F5";
                        const icon = e.target.querySelector("i");
                        if (icon) {
                          icon.style.backgroundColor = "#F8F5F5";
                        }
                      }}
                    >
                      {formatDateRange(dateRange)}
                      <i
                        className={`bi bi-chevron-down ${
                          dropdownVisible ? "rotate" : ""
                        }`}
                        style={{ marginLeft: "10px" }}
                      ></i>
                    </CButton>
                    {dropdownVisible && (
                      <CCard
                        className="border-light"
                        style={{
                          position: "absolute",
                          top: "50px",
                          zIndex: 1000,
                          width: "100%",
                          maxWidth: "400px",
                        }}
                      >
                        <CCardBody style={{ overflowX: "auto" }}>
                          <span>Filter By Date Range</span>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-start",
                              padding: "10px",
                              borderRadius: "5px",
                              width: "100%",
                              margin: "0px auto",
                              position: "relative",
                            }}
                          >
                            <Flatpickr
                              options={{ mode: "range", dateFormat: "d/m/Y" }}
                              value={tempDateRange}
                              onChange={handleDateChange}
                              className="form-control"
                              style={{
                                border: "1px solid lightgrey",
                                backgroundColor: "white",
                                color: "#555E67",
                                borderRadius: "10px",
                                width: "100%",
                                height: "45px",
                                transition: "background-color 0.3s",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor = "#e0e0e0")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor = "white")
                              }
                            />
                            <i
                              className={`bi bi-calendar`}
                              style={{
                                position: "absolute",
                                right: "20px",
                                top: "50%",
                                transform: "translateY(-50%)",
                              }}
                            ></i>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-around",
                              marginTop: "0px",
                            }}
                          >
                            <CButton
                              color="secondary"
                              onClick={() =>
                                handleButtonClick(setDateRangeToToday, "today")
                              }
                              style={{
                                border: "1px solid lightgrey",
                                backgroundColor:
                                  activeButton === "today"
                                    ? "#0B4FE4"
                                    : "white",
                                borderRadius: "10px",
                                color:
                                  activeButton === "today"
                                    ? "white"
                                    : "#555E67",
                                width: "23%",
                                height: "60px",
                                cursor: "pointer",
                                transition: "background-color 0.3s",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "today"
                                    ? "#0056b3"
                                    : "#e0e0e0")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "today"
                                    ? "#007bff"
                                    : "white")
                              }
                            >
                              Today
                            </CButton>
                            <CButton
                              color="secondary"
                              onClick={() =>
                                handleButtonClick(
                                  setDateRangeToThisWeek,
                                  "thisWeek"
                                )
                              }
                              style={{
                                border: "1px solid lightgrey",
                                backgroundColor:
                                  activeButton === "thisWeek"
                                    ? "#0B4FE4"
                                    : "white",
                                borderRadius: "10px",
                                color:
                                  activeButton === "thisWeek"
                                    ? "white"
                                    : "#555E67",
                                width: "23%",
                                height: "60px",
                                cursor: "pointer",
                                transition: "background-color 0.3s",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "thisWeek"
                                    ? "#0056b3"
                                    : "#e0e0e0")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "thisWeek"
                                    ? "#007bff"
                                    : "white")
                              }
                            >
                              This Week
                            </CButton>
                            <CButton
                              color="secondary"
                              onClick={() =>
                                handleButtonClick(
                                  setDateRangeToThisMonth,
                                  "thisMonth"
                                )
                              }
                              style={{
                                border: "1px solid lightgrey",
                                backgroundColor:
                                  activeButton === "thisMonth"
                                    ? "#0B4FE4"
                                    : "white",
                                borderRadius: "10px",
                                color:
                                  activeButton === "thisMonth"
                                    ? "white"
                                    : "#555E67",
                                width: "23%",
                                height: "60px",
                                cursor: "pointer",
                                transition: "background-color 0.3s",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "thisMonth"
                                    ? "#0056b3"
                                    : "#e0e0e0")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "thisMonth"
                                    ? "#007bff"
                                    : "white")
                              }
                            >
                              This Month
                            </CButton>
                            <CButton
                              color="secondary"
                              onClick={() =>
                                handleButtonClick(
                                  setDateRangeToThisYear,
                                  "thisYear"
                                )
                              }
                              style={{
                                border: "1px solid lightgrey",
                                backgroundColor:
                                  activeButton === "thisYear"
                                    ? "#0B4FE4"
                                    : "white",
                                borderRadius: "10px",
                                color:
                                  activeButton === "thisYear"
                                    ? "white"
                                    : "#555E67",
                                width: "23%",
                                height: "60px",
                                cursor: "pointer",
                                transition: "background-color 0.3s",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "thisYear"
                                    ? "#0056b3"
                                    : "#e0e0e0")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "thisYear"
                                    ? "#007bff"
                                    : "white")
                              }
                            >
                              This Year
                            </CButton>
                          </div>
                          <hr></hr>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginTop: "10px",
                            }}
                          >
                            <CButton
                              color="secondary"
                              onClick={() => {
                                resetDateRange();
                                setActiveButton(null);
                              }}
                              style={{
                                border: "1px solid #0B4FE4",
                                backgroundColor: "white",
                                borderRadius: "5px",
                                color: "#0B4FE4",
                                width: "45%",
                                height: "50px",
                                cursor: "pointer",
                                transition: "background-color 0.3s",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor = "#e0e0e0")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor = "white")
                              }
                            >
                              Reset
                            </CButton>
                            <CButton
                              color="primary"
                              onClick={applyDateRange}
                              style={{
                                border: "1px solid lightgrey",
                                backgroundColor: "#0B4FE4",
                                borderRadius: "5px",
                                color: "white",
                                width: "45%",
                                height: "50px",
                                cursor: "pointer",
                                transition: "background-color 0.3s",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor = "#083bb3")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor = "#0B4FE4")
                              }
                            >
                              Apply
                            </CButton>
                          </div>
                        </CCardBody>
                      </CCard>
                    )}
                  </div>
                </div>
                <BarChart chartData={customChartData} xAxisType="months" />
            </div>
          )}
          {activeTab === "loggedInUsers" && (
            <div
              style={{
                margin: "5px",
                borderRadius: "10px",
                overflowX: "auto",
              }}
            >
                <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap">
                  <div>
                    <h3>
                      <strong>{formatNumber(totalLoggedIn)}</strong>
                      <span
                        style={{
                          marginLeft: "10px",
                          color:
                            loggedInPercentageChange >= 0 ? "green" : "red",
                          fontSize: "0.6em",
                        }}
                      >
                        ({loggedInPercentageChange >= 0 ? "+" : ""}
                        {formatPercentage(loggedInPercentageChange)}%)
                      </span>
                    </h3>
                    <span className="text-muted">
                      Total Number of Logged In Users
                    </span>
                  </div>
                  <div
                    ref={dropdownRef}
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "6px",
                      borderRadius: "5px",
                      width: "340px",
                      margin: "10px auto",
                      marginBottom: "-20px",
                      position: "relative",
                    }}
                  >
                    <CButton
                      style={{
                        border: "0px solid lightgrey",
                        backgroundColor: "#F8F5F5",
                        borderRadius: "5px",
                        color: "#343333",
                        height: "45px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        transition: "background-color 0.3s",
                      }}
                      color="primary"
                      onClick={() => setDropdownVisible(!dropdownVisible)}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#e0e0e0";
                        const icon = e.target.querySelector("i");
                        if (icon) {
                          icon.style.backgroundColor = "#e0e0e0";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#F8F5F5";
                        const icon = e.target.querySelector("i");
                        if (icon) {
                          icon.style.backgroundColor = "#F8F5F5";
                        }
                      }}
                    >
                      {formatDateRange(loggedInDateRange)}
                      <i
                        className={`bi bi-chevron-down ${
                          dropdownVisible ? "rotate" : ""
                        }`}
                        style={{ marginLeft: "10px" }}
                      ></i>
                    </CButton>
                    {dropdownVisible && (
                      <CCard
                        className="border-light"
                        style={{
                          position: "absolute",
                          top: "50px",
                          zIndex: 1000,
                          width: "100%",
                          maxWidth: "400px",
                        }}
                      >
                        <CCardBody style={{ overflowX: "auto" }}>
                          <span>Filter By Date Range</span>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-start",
                              padding: "10px",
                              borderRadius: "5px",
                              width: "100%",
                              margin: "0px auto",
                              position: "relative",
                            }}
                          >
                            <Flatpickr
                              options={{ mode: "range", dateFormat: "d/m/Y" }}
                              value={tempLoggedInDateRange}
                              onChange={handleLoggedInDateChange}
                              className="form-control"
                              style={{
                                border: "1px solid lightgrey",
                                backgroundColor: "white",
                                color: "#555E67",
                                borderRadius: "10px",
                                width: "100%",
                                height: "45px",
                                transition: "background-color 0.3s",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor = "#e0e0e0")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor = "white")
                              }
                            />
                            <i
                              className={`bi bi-calendar`}
                              style={{
                                position: "absolute",
                                right: "20px",
                                top: "50%",
                                transform: "translateY(-50%)",
                              }}
                            ></i>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-around",
                              marginTop: "0px",
                            }}
                          >
                            <CButton
                              color="secondary"
                              onClick={() =>
                                handleButtonClick(
                                  setLoggedInDateRangeToToday,
                                  "today"
                                )
                              }
                              style={{
                                border: "1px solid lightgrey",
                                backgroundColor:
                                  activeButton === "today"
                                    ? "#0B4FE4"
                                    : "white",
                                borderRadius: "10px",
                                color:
                                  activeButton === "today"
                                    ? "white"
                                    : "#555E67",
                                width: "23%",
                                height: "60px",
                                cursor: "pointer",
                                transition: "background-color 0.3s",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "today"
                                    ? "#0056b3"
                                    : "#e0e0e0")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "today"
                                    ? "#007bff"
                                    : "white")
                              }
                            >
                              Today
                            </CButton>
                            <CButton
                              color="secondary"
                              onClick={() =>
                                handleButtonClick(
                                  setLoggedInDateRangeToThisWeek,
                                  "thisWeek"
                                )
                              }
                              style={{
                                border: "1px solid lightgrey",
                                backgroundColor:
                                  activeButton === "thisWeek"
                                    ? "#0B4FE4"
                                    : "white",
                                borderRadius: "10px",
                                color:
                                  activeButton === "thisWeek"
                                    ? "white"
                                    : "#555E67",
                                width: "23%",
                                height: "60px",
                                cursor: "pointer",
                                transition: "background-color 0.3s",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "thisWeek"
                                    ? "#0056b3"
                                    : "#e0e0e0")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "thisWeek"
                                    ? "#007bff"
                                    : "white")
                              }
                            >
                              This Week
                            </CButton>
                            <CButton
                              color="secondary"
                              onClick={() =>
                                handleButtonClick(
                                  setLoggedInDateRangeToThisMonth,
                                  "thisMonth"
                                )
                              }
                              style={{
                                border: "1px solid lightgrey",
                                backgroundColor:
                                  activeButton === "thisMonth"
                                    ? "#0B4FE4"
                                    : "white",
                                borderRadius: "10px",
                                color:
                                  activeButton === "thisMonth"
                                    ? "white"
                                    : "#555E67",
                                width: "23%",
                                height: "60px",
                                cursor: "pointer",
                                transition: "background-color 0.3s",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "thisMonth"
                                    ? "#0056b3"
                                    : "#e0e0e0")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "thisMonth"
                                    ? "#007bff"
                                    : "white")
                              }
                            >
                              This Month
                            </CButton>
                            <CButton
                              color="secondary"
                              onClick={() =>
                                handleButtonClick(
                                  setLoggedInDateRangeToThisYear,
                                  "thisYear"
                                )
                              }
                              style={{
                                border: "1px solid lightgrey",
                                backgroundColor:
                                  activeButton === "thisYear"
                                    ? "#0B4FE4"
                                    : "white",
                                borderRadius: "10px",
                                color:
                                  activeButton === "thisYear"
                                    ? "white"
                                    : "#555E67",
                                width: "23%",
                                height: "60px",
                                cursor: "pointer",
                                transition: "background-color 0.3s",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "thisYear"
                                    ? "#0056b3"
                                    : "#e0e0e0")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor =
                                  activeButton === "thisYear"
                                    ? "#007bff"
                                    : "white")
                              }
                            >
                              This Year
                            </CButton>
                          </div>
                          <hr></hr>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginTop: "10px",
                            }}
                          >
                            <CButton
                              color="secondary"
                              onClick={() => {
                                resetLoggedInDateRange();
                                setActiveButton(null);
                              }}
                              style={{
                                border: "1px solid #0B4FE4",
                                backgroundColor: "white",
                                borderRadius: "5px",
                                color: "#0B4FE4",
                                width: "45%",
                                height: "50px",
                                cursor: "pointer",
                                transition: "background-color 0.3s",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor = "#e0e0e0")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor = "white")
                              }
                            >
                              Reset
                            </CButton>
                            <CButton
                              color="primary"
                              onClick={applyLoggedInDateRange}
                              style={{
                                border: "1px solid lightgrey",
                                backgroundColor: "#0B4FE4",
                                borderRadius: "5px",
                                color: "white",
                                width: "45%",
                                height: "50px",
                                cursor: "pointer",
                                transition: "background-color 0.3s",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.backgroundColor = "#083bb3")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.backgroundColor = "#0B4FE4")
                              }
                            >
                              Apply
                            </CButton>
                          </div>
                        </CCardBody>
                      </CCard>
                    )}
                  </div>
                </div>
                <BarChart
                  chartData={customLoggedInChartData}
                  seriesName="Logged In Users"
                  xAxisType="months"
                />
            </div>
          )}
        </div>
      </CCardBody>
    </CCard>
  );
};

export default RegisteredUsersCard;
